"use client";

import { motion, useAnimation, useReducedMotion, Variants } from "framer-motion";
import { forwardRef, useCallback, useImperativeHandle, useRef } from "react";

export interface UsersHandle {
  startAnimation: () => void;
  stopAnimation: () => void;
}

interface UsersProps {
  size?: number;
  duration?: number;
  isAnimated?: boolean;
  isHovered?: boolean;
  className?: string;
}

const UsersIcon = forwardRef<UsersHandle, UsersProps>(
  (
    {
      className,
      size = 24,
      duration = 1,
      isAnimated = true,
      isHovered = false,
    },
    ref,
  ) => {
    const controls = useAnimation();
    const reduced = useReducedMotion();
    const isControlled = useRef(false);
    const prevHovered = useRef(isHovered);

    useImperativeHandle(ref, () => {
      isControlled.current = true;
      return {
        startAnimation: () =>
          reduced ? controls.start("normal") : controls.start("animate"),
        stopAnimation: () => controls.start("normal"),
      };
    });

    // React to isHovered prop changes
    if (isHovered !== prevHovered.current) {
      prevHovered.current = isHovered;
      if (isHovered && isAnimated && !reduced) {
        controls.start("animate");
      } else if (!isHovered) {
        controls.start("normal");
      }
    }

    const handleEnter = useCallback(() => {
      if (!isAnimated || reduced) return;
      if (!isControlled.current) controls.start("animate");
    }, [controls, reduced, isAnimated]);

    const handleLeave = useCallback(() => {
      if (!isControlled.current) controls.start("normal");
    }, [controls]);

    const arcVariants: Variants = {
      normal: { strokeDashoffset: 0, opacity: 1 },
      animate: {
        strokeDashoffset: [50, 0],
        opacity: [0.3, 1],
        transition: {
          duration: 0.7 * duration,
          ease: "easeInOut",
        },
      },
    };

    const headVariants: Variants = {
      normal: { scale: 1, opacity: 1 },
      animate: {
        scale: [0.6, 1.2, 1],
        opacity: [0, 1],
        transition: {
          duration: 0.6 * duration,
          ease: "easeOut",
        },
      },
    };

    const sideArcVariants: Variants = {
      normal: { strokeDashoffset: 0, opacity: 0.8 },
      animate: {
        strokeDashoffset: [40, 0],
        opacity: [0.2, 1],
        transition: {
          duration: 0.7 * duration,
          ease: "easeInOut",
          delay: 0.3,
        },
      },
    };

    return (
      <motion.svg
        xmlns="http://www.w3.org/2000/svg"
        width={size}
        height={size}
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
        className={className}
        onMouseEnter={handleEnter}
        onMouseLeave={handleLeave}
        style={{ flexShrink: 0 }}
      >
        <motion.path
          d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2"
          strokeDasharray="50"
          strokeDashoffset="50"
          variants={arcVariants}
          initial="normal"
          animate={controls}
        />
        <motion.path
          d="M16 3.128a4 4 0 0 1 0 7.744"
          strokeDasharray="40"
          strokeDashoffset="40"
          variants={sideArcVariants}
          initial="normal"
          animate={controls}
        />
        <motion.path
          d="M22 21v-2a4 4 0 0 0-3-3.87"
          strokeDasharray="40"
          strokeDashoffset="40"
          variants={sideArcVariants}
          initial="normal"
          animate={controls}
        />
        <motion.circle
          cx="9"
          cy="7"
          r="4"
          variants={headVariants}
          initial="normal"
          animate={controls}
        />
      </motion.svg>
    );
  },
);

UsersIcon.displayName = "UsersIcon";
export { UsersIcon };
